import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { Empty } from "@/components/Common";
import { DatePicker } from "@/components/DatePicker";

const NONE = "__none__";

export const FieldInput = ({ f, value, onChange, testPrefix }) => {
  const tid = `${testPrefix}-field-${f.key}`;
  if (f.type === "select") {
    return (
      <Select value={value || (f.optional ? NONE : undefined)} onValueChange={(v) => onChange(v === NONE ? null : v)}>
        <SelectTrigger className="mt-1.5" data-testid={tid}><SelectValue placeholder="Seleziona" /></SelectTrigger>
        <SelectContent>
          {f.optional && <SelectItem value={NONE}>— Nessuno —</SelectItem>}
          {f.options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    );
  }
  if (f.type === "multi") {
    const list = value || [];
    return (
      <div className="mt-1.5 grid max-h-44 grid-cols-2 gap-2 overflow-y-auto border p-3" data-testid={tid}>
        {f.options.map((o) => (
          <label key={o.value} className="flex items-center gap-2 text-sm">
            <Checkbox checked={list.includes(o.value)} onCheckedChange={(c) => onChange(c ? [...list, o.value] : list.filter((x) => x !== o.value))} />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  if (f.type === "custom") return <div className="mt-1.5">{f.render(value, onChange)}</div>;
  if (f.type === "tags") return <Input className="mt-1.5" value={(value || []).join(", ")} onChange={(e) => onChange(e.target.value.split(",").map((s) => s.trimStart()).filter((s, i, a) => s || i === a.length - 1))} onBlur={(e) => onChange(e.target.value.split(",").map((s) => s.trim()).filter(Boolean))} data-testid={tid} placeholder="Valori separati da virgola" />;
  if (f.type === "number") return <Input className="mt-1.5" type="number" step="0.01" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))} data-testid={tid} />;
  if (f.type === "switch") return <div className="mt-2"><Switch checked={!!value} onCheckedChange={onChange} data-testid={tid} /></div>;
  if (f.type === "date") return <div className="mt-1.5"><DatePicker value={value || ""} onChange={(v) => onChange(v || null)} clearable testId={tid} /></div>;
  return <Input className="mt-1.5" type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={tid} placeholder={f.placeholder} />;
};

export const CrudSection = ({ endpoint, title, fields, columns, defaults, canWrite = true, canDelete = false, testPrefix, onChange, prepare, query = "", openId = null, onOpened, filter }) => {
  const [all, setItems] = useState([]);
  const items = filter ? all.filter(filter) : all;
  const [q, setQ] = useState("");
  const [sort, setSort] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const load = useCallback(() => api.get(`/${endpoint}${query}`).then((r) => setItems(r.data)).catch((e) => toast.error(errMsg(e))), [endpoint, query]);
  useEffect(() => { load(); }, [load]);

  const open = (item) => { setEditing(item || "new"); setForm(item ? { ...item, password: "" } : { ...defaults }); };
  useEffect(() => { const it = openId && items.find((x) => x.id === openId); if (it) { open(it); onOpened?.(); } }, [openId, items]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    try {
      const body = prepare ? prepare(form) : form;
      if (editing === "new") await api.post(`/${endpoint}`, body);
      else await api.put(`/${endpoint}/${editing.id}`, body);
      toast.success("Salvato");
      setEditing(null);
      load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (item) => {
    if (!window.confirm("Eliminare l'elemento selezionato?")) return;
    try {
      await api.delete(`/${endpoint}/${item.id}`);
      load();
      onChange?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid={`${testPrefix}-section`}>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="font-display text-xl font-bold">{title}</h2>
        {(items.length > 15 || q) && <Input className="ml-auto w-64 bg-white" placeholder="Cerca…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testPrefix}-search`} />}
        {canWrite && <Button onClick={() => open(null)} data-testid={`${testPrefix}-add-button`}><Plus className="mr-1 h-4 w-4" />Nuovo</Button>}
      </div>
      {items.length === 0 ? <Empty text="Nessun elemento" /> : (
        <div className="overflow-x-auto border bg-white">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">{columns.map((c) => <TableHead key={c.label}>{c.sortKey ? (
              <button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setSort((s) => ({ key: c.sortKey, dir: s?.key === c.sortKey && s.dir === "asc" ? "desc" : "asc" }))} data-testid={`${testPrefix}-sort-${c.sortKey}`}>
                {c.label}{sort?.key === c.sortKey ? (sort.dir === "asc" ? " ↑" : " ↓") : " ↕"}
              </button>) : c.label}</TableHead>)}<TableHead className="w-24" /></TableRow></TableHeader>
            <TableBody>
              {(q ? items.filter((it) => Object.values(it).some((v) => typeof v === "string" && v.toLowerCase().includes(q.toLowerCase()))) : items)
                .slice().sort((a, b) => (sort ? (String(a[sort.key] || "") < String(b[sort.key] || "") ? -1 : String(a[sort.key] || "") > String(b[sort.key] || "") ? 1 : 0) * (sort.dir === "asc" ? 1 : -1) : 0)).map((it) => (
                <TableRow key={it.id} data-testid={`${testPrefix}-row-${it.id}`}>
                  {columns.map((c) => <TableCell key={c.label} className="text-sm">{c.render(it)}</TableCell>)}
                  <TableCell className="text-right">
                    {canWrite && <Button size="icon" variant="ghost" onClick={() => open(it)} data-testid={`${testPrefix}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Button>}
                    {canDelete && <Button size="icon" variant="ghost" onClick={() => remove(it)} data-testid={`${testPrefix}-delete-${it.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto" data-testid={`${testPrefix}-dialog`}>
          <DialogHeader><DialogTitle className="font-display text-2xl">{editing === "new" ? "Nuovo" : "Modifica"} — {title}</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            {fields.map((f) => (
              <div key={f.key}>
                <Label className="micro-label">{f.label}</Label>
                <FieldInput f={f} value={form[f.key]} onChange={(v) => setForm((s) => ({ ...s, [f.key]: v }))} testPrefix={testPrefix} />
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={save} data-testid={`${testPrefix}-save-button`}>Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
